import type { LegendListRef } from "@legendapp/list/react";
import { remoteComposerDisabledReason } from "./remoteComposerAvailability";
import type { RemoteThreadDispatchSettings } from "./useRemoteThreadExecutionIntent";
import { useState, type ReactElement, type RefObject } from "react";
import type { CmmdRuntimeThreadReplica, ScopedThreadRef } from "@cmmd-forge/contracts";

import {
  type ComposerThreadDraftState,
  useComposerDraftStore,
  useComposerThreadDraft,
} from "../../composerDraftStore";
import { useSettings } from "../../hooks/useSettings";
import { DEFAULT_INTERACTION_MODE, DEFAULT_RUNTIME_MODE } from "../../types";
import type { ChatComposerHandle } from "./ChatComposerHandle";
import { ChatComposer } from "./ChatComposer";
import { RemoteWorktreeSetupPanel } from "./RemoteWorktreeSetupPanel";
import { ExpandedImageDialog } from "./ExpandedImageDialog";
import { useChatImagePreviewState } from "./useChatImagePreviewState";
import { useChatComposerBindings } from "./useChatComposerBindings";
import { useComposerModeControls } from "./useComposerModeControls";
import type { ComposerModelSelection } from "./useComposerModelSelection";
import type { ComposerDisabledReason } from "./composerTurnPlan";
import { remoteComposerInactiveControls } from "./remoteComposerInactiveControls";
import { parseStandaloneComposerSlashCommand } from "../../composer-logic";
import { deriveComposerPlanProgress } from "./deriveComposerPlanProgress";
import { deriveRemoteLatestTurn, deriveRemotePlanProgress } from "./remotePlanProgress";
import type { RemoteThreadComposerContext } from "./remoteThreadComposerContext";
import type { RemoteRuntimeConnectionState } from "./remoteRuntimeThreadConnection.logic";
import { updateRemoteThreadGoal } from "./updateRemoteThreadGoal";

export function RemoteRuntimeThreadComposer(props: {
  readonly composerRef: RefObject<ChatComposerHandle | null>;
  readonly listRef: RefObject<LegendListRef | null>;
  readonly isAtEndRef: RefObject<boolean>;
  readonly banner: ReactElement | null;
  readonly connectionState: RemoteRuntimeConnectionState;
  readonly historyOnly: boolean;
  readonly onExecutionIntent: (settings?: RemoteThreadDispatchSettings) => void;
  readonly resolvedTheme: "dark" | "light";
  readonly threadRef: ScopedThreadRef;
  readonly runtimeId?: string;
  readonly context: RemoteThreadComposerContext;
  readonly replica: CmmdRuntimeThreadReplica | null;
  readonly modelSelection: ComposerModelSelection;
  readonly onOpenUsageLimits: () => boolean;
}): ReactElement {
  const handle = props.composerRef;
  const bindings = useChatComposerBindings();
  const draft = useComposerThreadDraft(props.threadRef);
  const settings = useSettings();
  const [error, setError] = useState<string | null>(null);
  const preview = useChatImagePreviewState();

  const runtimeMode = draft.runtimeMode ?? DEFAULT_RUNTIME_MODE;
  const interactionMode = draft.interactionMode ?? DEFAULT_INTERACTION_MODE;
  const lockedProvider = props.context.lockedProvider ?? null;
  const resolvedProvider = resolveRemoteComposerProvider(props.context, draft.activeProvider);
  const selection = props.modelSelection;
  const focus = () => handle.current?.focusAtEnd();
  const modes = useComposerModeControls({
    composerDraftTarget: props.threadRef,
    isLocalDraftThread: false,
    runtimeMode,
    interactionMode,
    setComposerDraftRuntimeMode: bindings.setComposerDraftRuntimeMode,
    setComposerDraftInteractionMode: bindings.setComposerDraftInteractionMode,
    setDraftThreadContext: bindings.setDraftThreadContext,
    scheduleComposerFocus: focus,
  });
  const presentation = remoteComposerPresentation(
    props.connectionState,
    draft.stagedSubmission !== null,
    props.context.workspace,
    props.replica,
  );
  const remoteLatestTurn = deriveRemoteLatestTurn(props.replica);
  const planProgress = deriveComposerPlanProgress(
    deriveRemotePlanProgress(props.replica),
    remoteLatestTurn.turnId,
    remoteLatestTurn.settled,
  );
  const connecting = presentation.connecting;
  const disabledReason = remoteComposerDisabledReason(
    props.historyOnly,
    props.connectionState,
    props.context.disabledReason,
  );

  const submit = (event?: { preventDefault: () => void }) => {
    event?.preventDefault();
    const currentDraft = useComposerDraftStore.getState().getComposerDraft(props.threadRef);
    if (tryHandleRemoteUsageLimitsCommand(currentDraft?.prompt, props)) return;
    if (!canDispatchRemoteComposerDraft(currentDraft, disabledReason, connecting)) return;
    props.onExecutionIntent({
      modelSelection: selection.modelSelection,
      runtimeMode,
      interactionMode,
    });
  };

  return (
    <div className="min-w-0 shrink-0 px-3 pt-1.5 pb-3 sm:px-5 sm:pt-2 sm:pb-4">
      <div className="mx-auto w-full min-w-0 max-w-3xl" data-testid="remote-thread-composer">
        {props.banner}
        <RemoteWorktreeSetupPanel replica={props.replica} />
        {error ? (
          <p role="alert" className="pb-2 text-sm text-destructive-foreground">
            {error}
          </p>
        ) : null}
        <ChatComposer
          {...remoteComposerInactiveControls}
          ref={handle}
          composerDraftTarget={props.threadRef}
          environmentId={props.threadRef.environmentId}
          routeKind="server"
          routeThreadRef={props.threadRef}
          draftId={null}
          activeThreadId={props.threadRef.threadId}
          activeThreadEnvironmentId={props.threadRef.environmentId}
          remoteGoal={
            props.context.threadGoal && props.runtimeId
              ? {
                  ...props.context.threadGoal,
                  onUpdateGoal: (goal) =>
                    updateRemoteThreadGoal({
                      environmentId: props.threadRef.environmentId,
                      runtimeId: props.runtimeId!,
                      threadId: props.threadRef.threadId,
                      goal,
                    }),
                }
              : null
          }
          isConnecting={connecting}
          planProgress={planProgress}
          contentPresentation={presentation.contentPresentation}
          runtimeMode={runtimeMode}
          interactionMode={interactionMode}
          resolvedProvider={resolvedProvider}
          lockedProvider={lockedProvider}
          providerStatuses={[...props.context.providers]}
          providerAvailability={props.context.availability}
          orgProviderPolicy={props.context.policy}
          activeThreadModelSelection={props.context.modelSelection}
          activeProjectTitle={presentation.projectTitle}
          activeThreadBranch={presentation.branch}
          activeThreadWorktreePath={presentation.worktreePath}
          activeThreadEnvMode={presentation.envMode}
          composerDisabledReason={presentation.disabledReason ?? disabledReason}
          resolvedTheme={props.resolvedTheme}
          settings={settings}
          promptRef={bindings.promptRef}
          composerImagesRef={bindings.composerImagesRef}
          composerTerminalContextsRef={bindings.composerTerminalContextsRef}
          shouldAutoScrollRef={props.isAtEndRef}
          scheduleStickToBottom={() => {
            if (props.isAtEndRef.current) props.listRef.current?.scrollToEnd({ animated: false });
          }}
          getTimelineScrollNode={() => props.listRef.current?.getScrollableNode() ?? null}
          isTimelineAtEnd={() => props.isAtEndRef.current}
          onSend={submit}
          onOpenUsageLimits={props.onOpenUsageLimits}
          onProviderModelSelect={(provider, model) => {
            const current = selection.modelSelection;
            useComposerDraftStore
              .getState()
              .setModelSelection(
                props.threadRef,
                current.provider === provider ? { ...current, model } : { provider, model },
              );
            focus();
          }}
          {...modes}
          focusComposer={focus}
          scheduleComposerFocus={focus}
          setThreadError={(_threadId, message) => setError(message)}
          onExpandImage={preview.onExpandTimelineImage}
        />
        {preview.expandedImage ? (
          <ExpandedImageDialog
            preview={preview.expandedImage}
            onClose={preview.closeExpandedImage}
          />
        ) : null}
      </div>
    </div>
  );
}

export function handleRemoteUsageLimitsCommand(input: {
  readonly prompt: string;
  readonly open: () => boolean;
  readonly clear: () => void;
}): boolean {
  if (parseStandaloneComposerSlashCommand(input.prompt) !== "usage-limits") return false;
  if (input.open()) input.clear();
  return true;
}

function tryHandleRemoteUsageLimitsCommand(
  prompt: string | undefined,
  props: Pick<Parameters<typeof RemoteRuntimeThreadComposer>[0], "onOpenUsageLimits" | "threadRef">,
): boolean {
  if (prompt === undefined) return false;
  return handleRemoteUsageLimitsCommand({
    prompt,
    open: props.onOpenUsageLimits,
    clear: () => useComposerDraftStore.getState().clearComposerContent(props.threadRef),
  });
}

export function canDispatchRemoteComposerDraft(
  draft: ComposerThreadDraftState | null,
  disabledReason: ComposerDisabledReason,
  connecting: boolean,
): boolean {
  return Boolean(
    // `false` is a held send with deliberately no copy (the verdict still
    // loading), so the null check, not truthiness, is what gates dispatch.
    disabledReason === null &&
    !connecting &&
    draft &&
    (draft.prompt.trim() || draft.images.length || (draft.files?.length ?? 0) > 0),
  );
}

const PREPARING_STATES: ReadonlySet<RemoteRuntimeConnectionState["kind"]> = new Set([
  "connecting",
  "retrying",
]);
const SUBMISSION_STATES: ReadonlySet<RemoteRuntimeConnectionState["kind"]> = new Set([
  "connecting",
  "retrying",
  "failed",
]);

function remoteComposerPresentation(
  connection: RemoteRuntimeConnectionState,
  hasSubmission: boolean,
  workspace: RemoteThreadComposerContext["workspace"],
  replica: CmmdRuntimeThreadReplica | null,
) {
  const { project = { title: null }, thread = { branch: null } } = replica ?? {};
  const { worktreePath = null } = workspace ?? {};
  const connecting = PREPARING_STATES.has(connection.kind);
  const failedSubmission = connection.kind === "failed" && hasSubmission;
  const contentPresentation: "submitted" | "draft" =
    SUBMISSION_STATES.has(connection.kind) && hasSubmission ? "submitted" : "draft";
  const envMode = workspace
    ? workspace.worktreePath
      ? ("worktree" as const)
      : ("local" as const)
    : null;
  return {
    projectTitle: project.title,
    branch: thread.branch,
    worktreePath,
    connecting,
    contentPresentation,
    envMode,
    disabledReason: failedSubmission ? "Retry the unsent message to continue." : null,
  };
}

export function resolveRemoteComposerProvider(
  context: RemoteThreadComposerContext,
  draftProvider: import("@cmmd-forge/contracts").ProviderKind | null,
) {
  return context.lockedProvider ?? draftProvider ?? context.modelSelection?.provider ?? "codex";
}
